import type { LegislativeFeedItem, LexData } from "./types";

// Tier C view-model for the Legislative action page: a real-data activity
// dashboard, a "needs attention" list (deadlines + contributors wanted), and a
// member-tailored legislative-news feed. All derived from data the app loads.

const DAY = 24 * 60 * 60 * 1000;
const QUARTER_MS = 90 * DAY;
const DEADLINE_WINDOW_DAYS = 21;

const COUNTRY_CODES: Record<string, string> = {
  Belgium: "BE", Germany: "DE", Switzerland: "CH", Sweden: "SE", Luxembourg: "LU", France: "FR",
  Netherlands: "NL", Italy: "IT", Spain: "ES", Poland: "PL", Austria: "AT", Portugal: "PT", Ireland: "IE",
  "United Kingdom": "UK", "United States": "US",
};
const codeFor = (jurisdiction: string) => COUNTRY_CODES[jurisdiction] ?? jurisdiction.slice(0, 2).toUpperCase();

export type LegislativeKpis = { positionsInFlight: number; activeMandates: number; outreachLogged: number; contributingFirms: number };
export type LegislativeTopFirm = { firmId: string; name: string; code: string; score: number; led: number; joined: number; endorsed: number };
export type DeadlineRow = { kind: "file" | "position"; id: string; title: string; daysLeft: number; label: string };
export type ContributorsWantedRow = { projectId: string; title: string; neededExpertise: string[]; jurisdiction: string };

export interface LegislativeOverview {
  kpis: LegislativeKpis;
  topFirms: LegislativeTopFirm[];
  contributorsWanted: ContributorsWantedRow[];
  deadlines: DeadlineRow[];
  news: LegislativeFeedItem[];
  watching: boolean;
}

export function buildLegislativeOverview(data: LexData, now = Date.now()): LegislativeOverview {
  const positions = data.lobbyingProjects;
  const participants = data.lobbyingProjectParticipants ?? [];
  const endorsements = data.lobbyingProjectEndorsements ?? [];
  const communications = data.lobbyingProjectCommunications ?? [];

  const kpis: LegislativeKpis = {
    positionsInFlight: positions.filter((position) => position.status !== "closed").length,
    activeMandates: positions.filter((position) => position.mandateStatus === "approved" || position.mandateStatus === "emergency").length,
    outreachLogged: communications.filter((communication) => now - communication.communicationDate <= QUARTER_MS).length,
    contributingFirms: new Set<string>([...positions.map((position) => position.ownerFirmId), ...participants.map((participant) => participant.firmId), ...endorsements.map((endorsement) => endorsement.firmId)]).size,
  };

  const led = new Map<string, number>();
  const joined = new Map<string, number>();
  const endorsed = new Map<string, number>();
  const bump = (map: Map<string, number>, key: string) => map.set(key, (map.get(key) ?? 0) + 1);
  positions.forEach((position) => bump(led, position.ownerFirmId));
  new Set(participants.map((participant) => `${participant.firmId}:${participant.projectId}`)).forEach((pair) => bump(joined, pair.split(":")[0]));
  endorsements.forEach((endorsement) => bump(endorsed, endorsement.firmId));
  const firmIds = new Set<string>([...led.keys(), ...joined.keys(), ...endorsed.keys()]);
  const topFirms: LegislativeTopFirm[] = [...firmIds]
    .map((firmId) => {
      const firm = data.firms.find((item) => item.id === firmId);
      const ledCount = led.get(firmId) ?? 0;
      const joinedCount = joined.get(firmId) ?? 0;
      const endorsedCount = endorsed.get(firmId) ?? 0;
      return { firmId, name: firm?.name ?? "A firm", code: firm ? codeFor(firm.jurisdiction) : "··", score: ledCount * 3 + joinedCount * 2 + endorsedCount, led: ledCount, joined: joinedCount, endorsed: endorsedCount };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  const contributorsWanted: ContributorsWantedRow[] = positions
    .filter((position) => position.helpWanted && position.status !== "closed")
    .map((position) => ({ projectId: position.id, title: position.title, neededExpertise: position.neededExpertise ?? [], jurisdiction: (position.targetInstitutions ?? [])[0] ?? "the network" }));

  const deadlineLabel = (daysLeft: number) => (daysLeft <= 0 ? "Due" : daysLeft === 1 ? "in 1 day" : `in ${daysLeft} days`);
  const fileDeadlines: DeadlineRow[] = data.legislationFiles
    .filter((file) => file.deadline)
    .map((file) => ({ kind: "file" as const, id: file.id, title: file.title, daysLeft: Math.round(((file.deadline ?? 0) - now) / DAY) }))
    .filter((row) => row.daysLeft <= DEADLINE_WINDOW_DAYS && row.daysLeft >= 0)
    .map((row) => ({ ...row, label: deadlineLabel(row.daysLeft) }));
  const positionDeadlines: DeadlineRow[] = positions
    .filter((position) => position.deadline && position.status !== "closed")
    .map((position) => ({ kind: "position" as const, id: position.id, title: position.title, daysLeft: Math.round(((position.deadline ?? 0) - now) / DAY) }))
    .filter((row) => row.daysLeft <= DEADLINE_WINDOW_DAYS && row.daysLeft >= 0)
    .map((row) => ({ ...row, label: deadlineLabel(row.daysLeft) }));
  const deadlines = [...fileDeadlines, ...positionDeadlines].sort((a, b) => a.daysLeft - b.daysLeft).slice(0, 6);

  // Member-tailored news from the legislative feed (visible, not rejected), filtered by watch preferences when set.
  const preference = data.legislativeMemberPreference ?? null;
  const watchedJurisdictions = new Set((preference?.watchedJurisdictions ?? []).map((value) => value.toLowerCase()));
  const watchedTopics = new Set((preference?.watchedTopics ?? []).map((value) => value.toLowerCase()));
  const watching = watchedJurisdictions.size > 0 || watchedTopics.size > 0;
  const news = (data.legislativeFeedItems ?? [])
    .filter((item) => item.visibilityStatus !== "hidden" && item.reviewStatus !== "rejected")
    .filter((item) => {
      if (!watching) return true;
      const jurisdictionMatch = item.jurisdiction ? watchedJurisdictions.has(item.jurisdiction.toLowerCase()) : false;
      const topicMatch = item.topics.some((topic) => watchedTopics.has(topic.toLowerCase()));
      return jurisdictionMatch || topicMatch;
    })
    .slice()
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 6);

  return { kpis, topFirms, contributorsWanted, deadlines, news, watching };
}
